import { useState, useRef, useEffect } from "react";

export default function ComboBox({ value, onChange, options = [], placeholder, id, disabled = false }) {
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const wrapperRef = useRef(null);

  const filtered = value
    ? options.filter((o) => o.toLowerCase().includes(value.toLowerCase()))
    : options;

  const showCustom =
    value.trim().length > 0 &&
    !options.some((o) => o.toLowerCase() === value.trim().toLowerCase());

  const totalOptions = filtered.length + (showCustom ? 1 : 0);

  useEffect(() => {
    function handleClick(e) {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  function selectCustom() {
    onChange(value.trim());
    setOpen(false);
  }

  function handleKeyDown(e) {
    if (!open) {
      if (e.key === "ArrowDown" || e.key === "Enter") {
        setOpen(true);
        setActiveIndex(0);
        e.preventDefault();
      }
      return;
    }
    if (e.key === "ArrowDown") {
      setActiveIndex((i) => Math.min(i + 1, totalOptions - 1));
      e.preventDefault();
    } else if (e.key === "ArrowUp") {
      setActiveIndex((i) => Math.max(i - 1, 0));
      e.preventDefault();
    } else if (e.key === "Enter") {
      if (activeIndex >= 0 && activeIndex < filtered.length) {
        onChange(filtered[activeIndex]);
        setOpen(false);
      } else if (activeIndex === filtered.length && showCustom) {
        selectCustom();
      }
      e.preventDefault();
    } else if (e.key === "Escape") {
      setOpen(false);
    }
  }

  return (
    <div className="combobox-wrapper" ref={wrapperRef}>
      <input
        id={id}
        className="combobox-input"
        type="text"
        value={value}
        placeholder={placeholder}
        autoComplete="off"
        disabled={disabled}
        onChange={(e) => {
          onChange(e.target.value);
          setOpen(true);
          setActiveIndex(-1);
        }}
        onFocus={() => !disabled && setOpen(true)}
        onKeyDown={handleKeyDown}
      />
      {open && (filtered.length > 0 || showCustom) && (
        <div className="combobox-dropdown">
          {filtered.slice(0, 80).map((opt, i) => (
            <div
              key={opt}
              className={"combobox-option" + (i === activeIndex ? " active" : "")}
              onMouseDown={(e) => {
                e.preventDefault();
                onChange(opt);
                setOpen(false);
              }}
              onMouseEnter={() => setActiveIndex(i)}
            >
              {opt}
            </div>
          ))}
          {showCustom && (
            <div
              className={"combobox-option combobox-option-custom" + (activeIndex === filtered.length ? " active" : "")}
              onMouseDown={(e) => {
                e.preventDefault();
                selectCustom();
              }}
              onMouseEnter={() => setActiveIndex(filtered.length)}
            >
              Use &ldquo;{value.trim()}&rdquo;
            </div>
          )}
        </div>
      )}
    </div>
  );
}
